import React, { useState, useMemo } from 'react';
import { Phone, Mail, Paperclip, Edit, ExternalLink, FileText } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Tooltip } from "reactstrap";
import { useNavigate } from 'react-router-dom';
import ContactLinksGenerator from "../../../utils/contactlinksgenerator";

interface LeadActionIconsProps {
  phone?: string;
  email?: string;
  onFileClick: () => void;
  onEditClick: (e: React.MouseEvent) => void;
  onFormResponseClick?: () => void;
  hasCustomForms?: boolean;
  contactLinksGenerator?: ContactLinksGenerator | null;
  leadId: string;
  leadName?: string;
  // TODO: Future WhatsApp Conversations Feature
  // onWhatsAppClick?: () => void;
}

const LeadActionIcons: React.FC<LeadActionIconsProps> = ({
  phone,
  email,
  onFileClick,
  onEditClick,
  onFormResponseClick,
  hasCustomForms,
  leadId,
  leadName = "Unknown Lead",
  contactLinksGenerator,
  // TODO: Future WhatsApp Conversations Feature
  // onWhatsAppClick,
}) => {
  const navigate = useNavigate();
  


  // Memoize the WhatsApp link for better performance
  const whatsappLink = useMemo(() => 
    contactLinksGenerator?.whatsapp || '#', 
    [contactLinksGenerator]
  );
  
  // Memoize the phone link
  const phoneLink = useMemo(() => `tel:+${phone}`, [phone]);
  
  // Memoize the email link
  const emailLink = useMemo(() => `mailto:${email}`, [email]);
  
  const [tooltipOpen, setTooltipOpen] = useState({
    phone: false,
    email: false,
    whatsapp: false,
    files: false,
    edit: false,
    external: false,
    quotation: false
  });

  type TooltipKey = 'phone' | 'email' | 'whatsapp' | 'files' | 'edit' | 'external' | 'quotation';
  
  const toggle = (name: TooltipKey) => {
    setTooltipOpen(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const isPhoneValid = phone && phone.length > 4;
  const isEmailValid = email && email.length > 8;
  
  const handleCreateQuotation = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/quotationlist', {
      state: {
        openCreateForm: true,
        leadForQuotation: {
          _id: leadId,
          name: leadName
        }
      }
    });
  };

  return (
    <ul className="popup-section-4-left-1 mt-1 text-center">
      <li>
        {isPhoneValid ? (
          <a href={phoneLink} id="phoneTooltip">
            <span className="link_icon" style={{ backgroundColor: "#0EA5E9" }}>
              <Phone size={18} />
            </span>
          </a>
        ) : (
          <span className="link_icon" style={{ backgroundColor: "#9CA3AF" }} id="phoneTooltip">
            <Phone size={16} />
          </span>
        )}
        <Tooltip
          isOpen={tooltipOpen.phone}
          target="phoneTooltip"
          toggle={() => toggle('phone')}
        >
          Call Lead
        </Tooltip>
      </li>

      <li>
        {isEmailValid ? (
          <a href={emailLink} id="emailTooltip">
            <span className="link_icon" style={{ backgroundColor: "#EAB308" }}>
              <Mail size={16} />
            </span>
          </a>
        ) : (
          <span className="link_icon" style={{ backgroundColor: "#adadad" }} id="emailTooltip">
            <Mail size={16} />
          </span>
        )}
        <Tooltip
          isOpen={tooltipOpen.email}
          target="emailTooltip"
          toggle={() => toggle('email')}
        >
          Send Email
        </Tooltip>
      </li>

      <li>
        {/* TODO: WhatsApp Conversations Feature - Currently opens new conversation via WhatsApp web */}
        {isPhoneValid ? (
          <a 
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            id="whatsappTooltip"
          >
            <span className="link_icon" style={{ backgroundColor: "#22C55E" }}>
              <FaWhatsapp size={16} />
            </span>
          </a>
          // TODO: Future implementation - Replace above <a> tag with button for popup
          // <button
          //   onClick={onWhatsAppClick}
          //   style={{ background: 'none', border: 'none', padding: 0 }}
          //   id="whatsappTooltip"
          // >
          //   <span className="link_icon" style={{ backgroundColor: "#22C55E" }}>
          //     <FaWhatsapp size={16} />
          //   </span>
          // </button>
        ) : (
          <span className="link_icon" style={{ backgroundColor: "#adadad" }} id="whatsappTooltip">
            <FaWhatsapp size={16} />
          </span>
        )}
        <Tooltip
          isOpen={tooltipOpen.whatsapp}
          target="whatsappTooltip"
          toggle={() => toggle('whatsapp')}
        >
          Send WhatsApp Message
          {/* TODO: Future - Change tooltip text to 'View WhatsApp Messages' when popup is enabled */}
        </Tooltip>
      </li>

      <li>
        <a onClick={onFileClick} id="filesTooltip">
          <span className="link_icon" style={{ backgroundColor: "#EF4444" }}>
            <Paperclip size={16} />
          </span>
        </a>
        <Tooltip
          isOpen={tooltipOpen.files}
          target="filesTooltip"
          toggle={() => toggle('files')}
        >
          Manage Files
        </Tooltip>
      </li>

      <li>
        <a onClick={onEditClick} id="editTooltip">
          <span className="link_icon" style={{ backgroundColor: "#3B82F6" }}>
            <Edit size={16} />
          </span>
        </a>
        <Tooltip
          isOpen={tooltipOpen.edit}
          target="editTooltip"
          toggle={() => toggle('edit')}
        >
          Edit Lead
        </Tooltip>
      </li>

      <li>
        {hasCustomForms ? (
          <a onClick={onFormResponseClick} id="externalTooltip">
            <span className="link_icon" style={{ backgroundColor: "#F87171" }}>
              <ExternalLink size={16} />
            </span>
          </a>
        ) : (
          <span className="link_icon" style={{ backgroundColor: "#adadad" }} id="externalTooltip">
            <ExternalLink size={16} />
          </span>
        )}
        <Tooltip
          isOpen={tooltipOpen.external}
          target="externalTooltip"
          toggle={() => toggle('external')}
        >
          View Form Response
        </Tooltip>
      </li>

      <li>
        <a onClick={handleCreateQuotation} id="quotationTooltip">
          <span className="link_icon" style={{ backgroundColor: "#8B5CF6" }}>
            <FileText size={16} />
          </span>
        </a>
        <Tooltip
          isOpen={tooltipOpen.quotation}
          target="quotationTooltip"
          toggle={() => toggle('quotation')}
        >
          Create Quotation
        </Tooltip>
      </li>
    </ul>
  );
};

// Wrap the component with React.memo to prevent unnecessary re-renders
export default React.memo(LeadActionIcons);